import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminApi, type AuditLogRow } from '@/api/admin';
import { toast } from 'sonner';
import { btnOutlineNeon, btnPrimaryNeon } from '@/lib/admin-ui';
import { usePlatformPermission } from '@/hooks/use-platform-permission';
import { useUIStore } from '@/store/useUIStore';
import { cn } from '@/lib/utils';

const ACTION_LABEL_MAP: Record<string, string> = {
  'platform_role.create': '创建平台角色',
  'platform_role.update': '更新平台角色',
  'platform_role.delete': '删除平台角色',
  'platform_user.create': '创建平台用户',
  'platform_user.update': '更新平台用户',
  'platform_user.delete': '删除平台用户',
  'platform_user.set_role': '变更平台用户角色',
  'tenant.update': '更新租户信息',
  'order.refund': '订单退款',
};

function getActionLabel(action: string): string {
  return ACTION_LABEL_MAP[action] ?? `操作：${action}`;
}

export function AuditLogsPage() {
  const canViewAudit = usePlatformPermission('platform:audit:view');
  const isLight = useUIStore((s) => s.theme === 'light');
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [actionFilter, setActionFilter] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!canViewAudit) {
      setRows([]);
      setTotalPages(0);
      setTotal(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await adminApi.getAuditLogs({
        page,
        limit,
        action: appliedFilter.trim() || undefined,
      });
      setRows(res.data);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message;
      const text = Array.isArray(msg) ? msg.join('; ') : msg;
      toast.error(
        typeof text === 'string' && text.length > 0
          ? text
          : '加载审计日志失败',
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilter, canViewAudit]);

  useEffect(() => {
    void load();
  }, [load]);

  function applyFilter() {
    setPage(1);
    setAppliedFilter(actionFilter);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">审计日志</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          记录平台租户编辑、平台用户与平台角色相关敏感操作；写入失败不影响主流程，以服务端记录为准。
        </p>
      </div>

      {!canViewAudit && (
        <div
          className={cn(
            'rounded-lg border px-4 py-3 text-sm',
            isLight
              ? 'border-amber-300 bg-amber-50 text-amber-800'
              : 'border-amber-500/30 bg-amber-500/10 text-amber-100',
          )}
        >
          当前角色无审计日志只读权限（
          <code
            className={cn(
              'font-mono',
              isLight ? 'text-amber-700' : 'text-amber-200/90',
            )}
          >
            platform:audit:view
          </code>
          ），下方列表为空。请联系管理员调整平台角色权限。
        </div>
      )}

      <Card
        className={cn(
          isLight ? 'border-slate-300 bg-white' : 'border-white/10 bg-zinc-900/40',
        )}
      >
        <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-lg">操作记录</CardTitle>
            <CardDescription>
              共 {total} 条；按动作码筛选（不区分大小写）
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-2">
              <Label htmlFor="audit-action">动作码包含</Label>
              <Input
                id="audit-action"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && canViewAudit && applyFilter()}
                placeholder="如 platform_user"
                disabled={!canViewAudit}
                className={cn(
                  'w-[200px]',
                  isLight
                    ? 'border-slate-300 bg-white text-slate-800 placeholder:text-slate-400'
                    : 'border-white/10 bg-zinc-950/60',
                )}
              />
            </div>
            <Button
              type="button"
              className={btnPrimaryNeon}
              disabled={!canViewAudit}
              onClick={applyFilter}
            >
              筛选
            </Button>
            <Button
              type="button"
              variant="outline"
              className={btnOutlineNeon}
              disabled={!canViewAudit}
              onClick={() => {
                setActionFilter('');
                setAppliedFilter('');
                setPage(1);
              }}
            >
              清除
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow
                    className={cn(
                      isLight
                        ? 'border-slate-300 hover:bg-transparent'
                        : 'border-white/10 hover:bg-transparent',
                    )}
                  >
                    <TableHead className="whitespace-nowrap">时间</TableHead>
                    <TableHead>操作者</TableHead>
                    <TableHead>操作动作</TableHead>
                    <TableHead>资源</TableHead>
                    <TableHead className="max-w-[280px]">详细数据</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.id}
                      className={cn(isLight ? 'border-slate-300' : 'border-white/10')}
                    >
                      <TableCell
                        className={cn(
                          'whitespace-nowrap font-mono text-xs',
                          isLight ? 'text-slate-500' : 'text-zinc-400',
                        )}
                      >
                        {r.createdAt.replace('T', ' ').slice(0, 19)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium text-foreground">{r.actorEmail}</div>
                        <div
                          className={cn(
                            'font-mono text-xs',
                            isLight ? 'text-slate-500' : 'text-zinc-500',
                          )}
                        >
                          {r.actorUserId}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div
                          className={cn(
                            'font-medium',
                            isLight ? 'text-cyan-700' : 'text-cyan-200/90',
                          )}
                        >
                          {getActionLabel(r.action)}
                        </div>
                        <div
                          className={cn(
                            'font-mono text-xs',
                            isLight ? 'text-slate-500' : 'text-zinc-500',
                          )}
                        >
                          {r.action}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className={cn(isLight ? 'text-slate-600' : 'text-zinc-400')}>
                          {r.resourceType}
                        </span>
                        {r.resourceId && (
                          <div
                            className={cn(
                              'mt-0.5 font-mono text-xs',
                              isLight ? 'text-slate-500' : 'text-zinc-500',
                            )}
                          >
                            {r.resourceId}
                          </div>
                        )}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'max-w-[280px] break-all font-mono text-[11px]',
                          isLight ? 'text-slate-500' : 'text-zinc-500',
                        )}
                      >
                        {r.metadata != null
                          ? JSON.stringify(r.metadata)
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length === 0 && (
                <p className="mt-4 text-sm text-muted-foreground">暂无记录</p>
              )}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between gap-2">
                  <p className="text-sm text-muted-foreground">
                    第 {page} / {totalPages} 页
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={btnOutlineNeon}
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      上一页
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={btnOutlineNeon}
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      下一页
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
